import assert from 'assert';
import React from 'react';
import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as contextmenu from 'components/ContextMenu';
import {
  EMPTY_SEARCH_RESULT_PROPS,
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {getLastSyncedUpstreamValueWithColumnConstraint} from 'notebook/structured-tables/util/getLastSyncedUpstreamValue';
import TableAxisCell from 'notebook/tables/core-ui/TableAxisCell';
import TableCell, {TableCellProps} from 'notebook/tables/core-ui/TableCell';
import {
  cellFillProps,
  getCellProps,
  getTableCellAffixedProps,
  isCellSelected,
} from 'notebook/tables/core-ui/TableCellRenderUtils';
import {TableEditableClass} from 'notebook/tables/core-ui/TableEditable';
import TableGhostCell from 'notebook/tables/core-ui/TableGhostCell';
import {isRowSelected} from 'notebook/tables/core-ui/TableRowUtils';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {RowModel} from 'notebook/tables/store/models/RowModel';
import * as TableConstants from 'notebook/tables/TableConstants';
import {SelectionRange, TableDimension} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import useHasBeenInView from 'ui-library/hooks/useHasBeenInView';
import {RowState} from 'util/manageRowState';
import {Optional} from 'util/Types';

export interface TableRowProps extends MediocreSearchResultProps {
  // RowModel
  // TODO(BNCH-113857): Remove this once selectors are enabled
  // These are now all optional because they might come from the store if it is enabled
  // These will be removed once we switch over to selectors
  row?: Array<TableConstants.Cell>;
  columnConstraints?: Array<TableConstants.ColumnConstraint | null>;
  evaluatedRow?: Array<EvaluatedCell>;
  rowData?: TableConstants.RowData | null;
  rowProperties?: TableConstants.RowProperties | null;

  fileId: string;
  tableName: string;
  tableNamespace: string;
  getLastIndex: () => TableConstants.TableIndex;
  locked: boolean;
  isProtected?: boolean;
  canModifyDims: boolean;
  canModifyRowDim: boolean;
  allowNoRows?: boolean;
  i: number;
  rowState: RowState | null;
  renderedLabel?: string | JSX.Element | null;
  isHeaderRow: Optional<boolean>;
  headerRowHeight?: number;
  hasHeaderCol: Optional<boolean>;
  // Only set if we should show them.
  cellColors: Optional<{[columnIndex: number]: string}>;
  cellCid: (i: number, j: number) => string;
  ghostCellsCount: number;

  renderCellsOnly?: boolean;

  // Label
  disableFormulas?: boolean;
  showExcessLabels?: boolean;
  isLeftAlignRowHeader?: boolean;

  // A TableRow can be rendered partially (a "slice") given a start and an end index, which enables
  // rendering an affixed slice and a scrollable slice. The start and end index are restricted to the
  // normal bounds of a row (excluding leading columns, axis column, and ghost cells).
  // - sliceStart of null indicates including up to the first column
  // - sliceEnd of null indicates including up through all of the ghost cells
  sliceStart: number | null;
  sliceEnd: number | null;
  // Because a partial row does not contain all columns, it is not guaranteed that its rendered height
  // will match that of the full row, so we allow that height to be specified.
  rowHeight: Optional<number>;

  // Props for axis cell
  getCopySelection: TableEditableClass['_getCopySelection'];
  getCopySelectionHTML: TableEditableClass['_getCopySelectionHTML'];
  clearSelection: TableEditableClass['deleteRangeHelper'];
  // omit deleteRows if delete is not allowed, e.g. TableRow is the only row
  deleteRows: ((start: number | null, end: number | null) => void) | null;
  insertRowsAbove: (start: number | null, end: number | null) => void;
  insertRowsBelow: (start: number | null, end: number | null) => void;
  multiPasteHandler: TableEditableClass['multiPasteHandler'];
  hasLockedCellInSelection: TableEditableClass['hasLockedCellInSelection'];
  deleteTable: (() => void) | null;
  getExtraAxisCellContextMenuOptions?: (
    selectionRange: TableConstants.SelectionRange | null
  ) => Array<contextmenu.ContextMenuOption> | null;
  getSelectionRange: (dim: TableDimension, index: number) => SelectionRange | null;
  isTableSelected?: boolean;
  toggleHeaderRow?: () => void;
  numRows: number;
  lockedRowIndices?: Set<number>;
  filteredOutRowIndices?: Set<number>;
  showDragHandle?: boolean;

  // Props for selected cell
  hasActiveCell?: boolean; // selectedJ is active
  axisSelection?: TableConstants.MultiAxisDimSelection;
  selectedJ: number | null;
  selectedMinJ: number | null;
  selectedMaxJ: number | null;

  isFillDownBoxBottomEdge: boolean;
  isFillDownBoxTopEdge: boolean;
  isFilledDown: boolean;
  fillMinJ: number | null;
  fillMaxJ: number | null;

  affixedColumnCount?: number;
  firstColumnWidth?: number;
  columnWidths?: Array<number>;
  showAffixedShadow?: boolean;

  // Actions
  expandToAndEditCell: (index: TableConstants.TableIndex, selectAll?: boolean) => void;
  storeState: TableEditableClass['saveMultiSelect'];
  commitCell: TableEditableClass['updateCell'];
  moveFromCell: TableEditableClass['moveFromCell'];
  selectCell: TableEditableClass['selectCell'];

  // Event handlers
  axisCellDragOver: TableEditableClass['axisCellDragOverHandler'];
  axisCellDragStart: TableEditableClass['axisCellDragStartHandler'];
  axisCellDragHandleMouseDown: TableEditableClass['axisCellDragHandleMouseDown'];
  cellEnterHandler: TableEditableClass['cellEnterHandler'];
  cellEscapeHandler: TableEditableClass['cellEscapeHandler'];
  cellDragOver: TableEditableClass['cellDragOverHandler'];
  cellDragStart: TableEditableClass['multiEditableMouseDownHandler'];
  cellMouseMoveHandler: TableEditableClass['fillDownMouseMoveHandler'];
  // If present, last cell will render fillDown box
  fillDownSquareMouseDownHandler: TableEditableClass['fillDownSquareMouseDownHandler'] | null;
  fillDownSquareDoubleClickHandler: TableEditableClass['fillDownSquareDoubleClickHandler'] | null;
  keyDownHook: TableEditableClass['hideHoverCell'];

  // Selectors
  selectorsEnabled: boolean;
  openManageSelectorsModal: (initialSelectorId: string) => void;

  // Performance Optimizations
  calculateInView?: boolean;
}

export function renderTableCell(
  props: TableRowProps,
  rowModel: RowModel,
  j: number,
  hasRowBeenInView?: boolean
): JSX.Element | null {
  const {i} = props;
  const {isCellLocked, isCellSelected, isCellDblSelected, hasActiveEdit, cellTypedValue, isTombstoned} =
    getCellProps(props, rowModel.row, rowModel.evaluatedRow, rowModel.rowProperties, j);

  const columnConstraint = getColumnConstraintWithRowData(rowModel.columnConstraints[j], rowModel.rowData);

  const isNotFirstColumnInMerge = rowModel.evaluatedRow[j].mergedBox?.minJ !== j;
  if (TableUtil.isNotRendered(i, j, rowModel.evaluatedRow[j]) && isNotFirstColumnInMerge) {
    return null;
  }

  const colSpan = TableUtil.getColSpan(j, rowModel.row[j], rowModel.evaluatedRow[j]);
  const rowSpan = TableUtil.getRowSpan(i, rowModel.row[j], rowModel.evaluatedRow[j]);

  // If this is the first rendered row of a merge cell, then we must use the virtual row index of the first
  // row of the merge. The only merge cell we render must be the top left cell in the merge, or a simulated
  // version of it if the real top left cell is not rendered.
  let virtualRowIndex = i;
  const mergedBox = rowModel.evaluatedRow[j].mergedBox;
  if (mergedBox !== undefined) {
    virtualRowIndex = mergedBox.minI;
  }

  const lastSyncedUpstreamValue = getLastSyncedUpstreamValueWithColumnConstraint(
    rowModel.rowData?.lastSyncedUpstreamValues,
    columnConstraint
  );
  const tableCellAffixedProps = getTableCellAffixedProps({...props, j});
  return (
    <TableCell
      key={j}
      {...({
        i: virtualRowIndex,
        j,

        // TODO(BNCH-113857): Remove this once selectors are enabled
        // If we're not using selectors, then we need to pass the cell data directly
        ...(BenchlingConfig.ELN_TABLE_ENABLE_STATE_SELECTORS
          ? {}
          : {
              cell: {
                ...rowModel.row[j],
                text:
                  rowModel.row[j].text ||
                  (cellTypedValue != null && TableConstants.isTextEditableType(cellTypedValue)
                    ? formatTypedValue(cellTypedValue)
                    : ''),
                // grayOutContents is only set for StructuredTables
                ...(isTombstoned && {grayOutContents: true}),
              },
              evaluatedCell: rowModel.evaluatedRow[j],
              columnConstraint,
              rowData: rowModel.rowData,
            }),

        fileId: props.fileId,
        tableNamespace: props.tableNamespace,
        getLastIndex: props.getLastIndex,
        locked: isCellLocked,
        cid: props.cellCid(virtualRowIndex, j),
        isHeaderCell: Boolean(props.isHeaderRow || (props.hasHeaderCol && j === 0)),
        headerRowHeight: props.headerRowHeight,
        hasActiveEdit,
        lastSyncedUpstreamValue: lastSyncedUpstreamValue ?? undefined,
        rowSpan,
        colSpan,
        isHiddenMergedCell: virtualRowIndex !== i,

        // If we have formula cell coloring and we are actively editing a cell, show the cell color.
        highlightClass: props.cellColors != null ? props.cellColors[j] : undefined,
        isFirstColumn: props.firstColumnWidth === 0 && j === 0,
        isSelected: isCellSelected,
        isDoubleSelected: isCellDblSelected,
        storeState: props.storeState,
        commitCell: props.commitCell,
        selectCell: props.selectCell,
        getCopySelection: props.getCopySelection,
        getCopySelectionHTML: props.getCopySelectionHTML,
        clearSelection: props.clearSelection,
        multiPasteHandler: props.multiPasteHandler,
        hasLockedCellInSelection: props.hasLockedCellInSelection,
        fillDownSquareMouseDownHandler:
          props.fillDownSquareMouseDownHandler != null && j === props.selectedMaxJ
            ? props.fillDownSquareMouseDownHandler
            : null,
        fillDownSquareDoubleClickHandler:
          props.fillDownSquareDoubleClickHandler && j === props.selectedMaxJ
            ? props.fillDownSquareDoubleClickHandler
            : null,
        cellDragStart: props.cellDragStart,
        cellDragOver: props.cellDragOver,
        cellMouseMoveHandler: props.cellMouseMoveHandler,

        selectorsEnabled: props.selectorsEnabled,
        openManageSelectorsModal: props.openManageSelectorsModal,
        hasRowBeenInView,
        ...tableCellAffixedProps,
        ...cellFillProps(props, j),
        ...getSearchResultPropsForIndex(props, j),
      } satisfies TableCellProps)}
    />
  );
}

/**
 * A data row in a TableEditable, starting with an Excel row label (1, 2, 3, ...)
 */
function TableRow({
  allowNoRows = false,
  calculateInView = BenchlingConfig.ELN_ENABLE_TABLE_ROW_CALCULATE_IN_VIEW || false,
  ...props
}: TableRowProps): JSX.Element | null {
  const {hasBeenInView: hasRowBeenInView, ref} = useHasBeenInView(calculateInView);
  const rowModelFromStore = useRowModel();

  // TODO(BNCH-113857): Remove this once selectors are enabled
  // For now, rowModel may be null if selectors are disabled.  Pass along the props if so
  let rowModel: RowModel;
  if (rowModelFromStore) {
    rowModel = rowModelFromStore;
  } else {
    assert(props.row !== undefined && props.evaluatedRow !== undefined, 'Row data must be provided');
    rowModel = {
      row: props.row,
      evaluatedRow: props.evaluatedRow,
      // Casting these since they were previously optional but are now required by the RowModel interface
      rowProperties: props.rowProperties as TableConstants.RowProperties,
      rowData: props.rowData as TableConstants.RowData,
      columnConstraints: props.columnConstraints as Array<TableConstants.ColumnConstraint | null>,
    };
  }

  function canDeleteRows(j: number): boolean {
    const {axisSelection, lockedRowIndices, filteredOutRowIndices} = props;
    if (lockedRowIndices == null) {
      return true;
    }
    const rowSelection = axisSelection?.row;
    if (!rowSelection) {
      return false;
    }
    const [rowSelectionStartIndex, rowSelectionEndIndex] = rowSelection;
    for (let i = rowSelectionStartIndex; i <= rowSelectionEndIndex; i++) {
      if (lockedRowIndices.has(i)) {
        // if the row is locked but is filtered out, do not return false
        if (filteredOutRowIndices?.has(i)) {
          continue;
        }
        return false;
      }
    }
    return true;
  }

  function renderAxisCell(hasRowBeenInView?: boolean): JSX.Element {
    return (
      <TableAxisCell
        key="axis"
        index={props.i}
        shouldHighlightCell={false}
        // Row labels do not currently support rename, so we are able to omit both `label` and `renameDim`
        renderedLabel={props.renderedLabel}
        disableFormulas={props.disableFormulas}
        showExcessLabels={props.showExcessLabels}
        dim={TableConstants.TABLE_ROW}
        toggleHeader={props.i === 0 && props.canModifyDims ? props.toggleHeaderRow : undefined}
        delDim={props.deleteRows}
        shouldDisableInsertDimBefore={false}
        shouldDisableInsertDimAfter={false}
        shouldDisableDeleteDim={!canDeleteRows(props.i)}
        insertDimBefore={props.canModifyRowDim ? props.insertRowsAbove : undefined}
        insertDimAfter={props.canModifyRowDim ? props.insertRowsBelow : undefined}
        getExtraContextMenuOptions={props.getExtraAxisCellContextMenuOptions}
        getSelectionRange={props.getSelectionRange}
        numDims={props.numRows}
        deleteTable={props.deleteTable}
        allowEmpty={allowNoRows}
        isTableSelected={props.isTableSelected}
        cellDragStart={props.axisCellDragStart}
        cellDragOver={props.axisCellDragOver}
        showDragHandle={props.showDragHandle}
        dragHandleMouseDown={props.axisCellDragHandleMouseDown}
        isSelected={isRowSelected(props)}
        rowData={rowModel.rowData}
        rowProperties={rowModel?.rowProperties}
        locked={props.locked}
        isProtected={props.isProtected}
        isLeftAlign={props.isLeftAlignRowHeader}
        isFirstColumn={props.firstColumnWidth ? props.firstColumnWidth > 0 : false}
        hasRowBeenInView={hasRowBeenInView}
        dataTestAttrs={{component: 'TableAxisCell', element: 'row'}}
        isAffixed={true}
        showAffixedShadow={props.showAffixedShadow && 0 === props.affixedColumnCount}
        affixedLeftSpacing={0}
        {...EMPTY_SEARCH_RESULT_PROPS}
      />
    );
  }

  function renderGhostCell(j: number): JSX.Element {
    const {i} = props;
    return (
      <TableGhostCell
        // eslint-disable-next-line no-restricted-syntax
        {..._.extend(
          {
            key: j,
            cid: props.cellCid(i, j),
            i,
            j,
            selectCell: props.selectCell,
            expandToAndEditCell: props.expandToAndEditCell,
            cellMouseMoveHandler: props.cellMouseMoveHandler,
            isHeaderRow: props.isHeaderRow,
            isSelected: isCellSelected(props, j),
          },
          cellFillProps(props, j)
        )}
      />
    );
  }

  const useSimpleTableVirtualization = !props.renderCellsOnly;
  const tableCells = TableUtil.mapColumns({
    sliceStart: props.sliceStart,
    sliceEnd: props.sliceEnd,

    ghostCellsCount: props.ghostCellsCount,
    normalCellsCount: rowModel.evaluatedRow.length,

    mapLeadingCell: () => renderAxisCell(useSimpleTableVirtualization ? hasRowBeenInView : true),
    mapNormalCell: (j: number) =>
      renderTableCell(props, rowModel, j, useSimpleTableVirtualization ? hasRowBeenInView : true),
    mapGhostCell: (j: number) => renderGhostCell(j),
  });
  if (props.renderCellsOnly) {
    // If we are rendering only the cells, we don't need to render the tr.  This will be rendered with
    // TableTr by the parent independently.
    return <>{tableCells}</>;
  }
  return (
    <tr
      ref={ref}
      className="mediocre-tableEditable-tableRow"
      style={{height: props.rowHeight || undefined}}
      {...getTestProps({component: 'TableRow', element: 'root', index: props.i.toString()})}
    >
      {tableCells}
    </tr>
  );
}

function WrappedTableRow(props: TableRowProps): JSX.Element | null {
  return (
    <RowModelProvider rowIndex={props.i}>
      <TableRow {...props} />
    </RowModelProvider>
  );
}

export default React.memo(
  WrappedTableRow,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    Object.keys(nextProps).every((key) => {
      // Function props get regenerated on rerenders, and isEqual will always return false for them.
      // It's safe to assume other props will change if we want a function prop change to trigger with a rerender.
      const isPropComponent = typeof prevProps[key] === 'function' && typeof nextProps[key] === 'function';
      // TODO(BNCH-113857): Switch back to shallow equal once selectors are enabled
      return (
        isPropComponent ||
        (BenchlingConfig.ELN_TABLE_ENABLE_STATE_SELECTORS
          ? shallowEqual(prevProps[key], nextProps[key])
          : isEqual(prevProps[key], nextProps[key]))
      );
    })
);
